import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography, alpha, useTheme } from '@mui/material';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useVenue } from '../../../../context/VenueContext';
import menuService from '../../../../services/menuService';
import menuItemService from '../../../../services/menuItemService';
import type { Menu as MenuDoc, MenuItemKind, MenuType } from '../../../../types/menu';
import MenuItemFormNonWine from './MenuItemFormNonWine';
import MenuItemFormWine from './MenuItemFormWine';

/**
 * Kind-specific MenuItem form dispatcher.
 *
 * Resolution order:
 *   1. Edit mode (itemId present): load the item, dispatch by item.kind.
 *      Unsupported kinds (DISH/BEER/SPIRIT/etc.) render an explicit
 *      "not yet supported" message — never fall through to a wrong sub-form.
 *   2. Create mode (no itemId):
 *        a. `?kind=WINE` or `?kind=COCKTAIL` query param wins.
 *        b. Otherwise infer from menu.type:
 *             - FULL / BY_THE_GLASS / RESERVE / EVENT / PRIVATE → WINE
 *             - COCKTAIL → COCKTAIL
 *             - MIXED / anything else → show a small kind picker.
 *
 * The sub-forms re-read useParams themselves; the dispatcher never passes
 * props, so route mounts (admin/venues/index.tsx) stay unchanged.
 */

const WINE_INFERRED_MENU_TYPES: MenuType[] = ['FULL', 'BY_THE_GLASS', 'RESERVE', 'EVENT', 'PRIVATE'];
// Phase 7.C.2b — FOOD menus author DISH items via the generic non-wine form.
const DISH_INFERRED_MENU_TYPES: MenuType[] = ['FOOD'];
const SUPPORTED_KINDS_PHASE_4_B: MenuItemKind[] = ['WINE', 'COCKTAIL', 'DISH'];

const MenuItemForm: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { menuId, itemId } = useParams<{ menuId: string; itemId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { venueId } = useVenue();
  const isEdit = Boolean(itemId);
  const backPath = `/admin/venues/${venueId}/menus/${menuId}`;

  const [menu, setMenu] = useState<MenuDoc | null>(null);
  const [itemKind, setItemKind] = useState<MenuItemKind | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  // ── Load menu (+ item, in edit mode) in parallel ─────────────────────────
  useEffect(() => {
    if (!menuId) return;
    let cancelled = false;
    setLoading(true);
    setLoadError('');

    const menuPromise = menuService.getMenu(venueId, menuId);
    const itemPromise = isEdit && itemId
      ? menuItemService.getItem(venueId, menuId, itemId)
      : Promise.resolve(null);

    Promise.all([menuPromise, itemPromise])
      .then(([m, item]) => {
        if (cancelled) return;
        setMenu(m);
        setItemKind(item ? item.kind : null);
      })
      .catch(() => {
        if (cancelled) return;
        setLoadError(isEdit ? 'Could not load this item.' : 'Could not load this menu.');
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [venueId, menuId, isEdit, itemId]);

  // ── Loading / error shells ───────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadError || !menu) {
    return (
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate(backPath)} sx={{ textTransform: 'none', color: theme.palette.text.secondary, alignSelf: 'flex-start' }}>
          Menu
        </Button>
        <Alert severity="error">{loadError || 'Menu not found.'}</Alert>
      </Box>
    );
  }

  // ── Edit mode: dispatch by item.kind ─────────────────────────────────────
  if (isEdit) {
    if (itemKind === 'WINE') return <MenuItemFormWine />;
    if (itemKind === 'COCKTAIL') return <MenuItemFormNonWine kind="COCKTAIL" />;
    if (itemKind === 'DISH') return <MenuItemFormNonWine kind="DISH" />;
    return (
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate(backPath)} sx={{ textTransform: 'none', color: theme.palette.text.secondary, alignSelf: 'flex-start' }}>
          Menu
        </Button>
        <Alert severity="warning">
          Editing items of kind <code>{itemKind ?? 'UNKNOWN'}</code> is not supported in this phase.
        </Alert>
      </Box>
    );
  }

  // ── Create mode: ?kind query → menu.type inference → kind picker ─────────
  const queryKindRaw = searchParams.get('kind');
  const queryKind = (queryKindRaw && SUPPORTED_KINDS_PHASE_4_B.includes(queryKindRaw as MenuItemKind))
    ? (queryKindRaw as MenuItemKind)
    : null;

  if (queryKind === 'WINE') return <MenuItemFormWine />;
  if (queryKind === 'COCKTAIL') return <MenuItemFormNonWine kind="COCKTAIL" />;
  if (queryKind === 'DISH') return <MenuItemFormNonWine kind="DISH" />;

  // No ?kind — infer from menu type.
  if (WINE_INFERRED_MENU_TYPES.includes(menu.type)) return <MenuItemFormWine />;
  if (menu.type === 'COCKTAIL') return <MenuItemFormNonWine kind="COCKTAIL" />;
  if (DISH_INFERRED_MENU_TYPES.includes(menu.type)) return <MenuItemFormNonWine kind="DISH" />;

  // MIXED or any other type — small kind picker (no auto-dispatch).
  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <Box sx={{ px: 3, py: 2.5, display: 'flex', alignItems: 'center', gap: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}` }}>
          <Button startIcon={<BackIcon />} onClick={() => navigate(backPath)} sx={{ textTransform: 'none', color: theme.palette.text.secondary }}>
            Menu
          </Button>
          <Typography variant="h6" fontWeight={600}>Add item</Typography>
        </Box>
        <Box sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Pick what to add to this {menu.type.replace(/_/g, ' ').toLowerCase()} menu.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Button
              variant="contained"
              onClick={() => setSearchParams({ kind: 'WINE' })}
              sx={{ textTransform: 'none', fontWeight: 600, flex: 1 }}
            >
              Add Wine
            </Button>
            <Button
              variant="contained"
              onClick={() => setSearchParams({ kind: 'COCKTAIL' })}
              sx={{ textTransform: 'none', fontWeight: 600, flex: 1 }}
            >
              Add Cocktail
            </Button>
            <Button
              variant="contained"
              onClick={() => setSearchParams({ kind: 'DISH' })}
              sx={{ textTransform: 'none', fontWeight: 600, flex: 1 }}
            >
              Add Dish
            </Button>
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
};

export default MenuItemForm;
